import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import PropTypes from "prop-types";
import { Close, Success, Copy } from "@washingtonpost/wpds-assets";
import {
  DialogBody,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogRoot,
  DialogTitle,
  Icon
} from "@washingtonpost/wpds-ui-kit";

import { copyTextToClipboard } from "../ShareButton/utils";
import {
  EMBED_DIMENSION_PRESETS,
  getEmbedIframeHtml,
  matchesEmbedPreset,
  mergeEmbedTimeQueryParam,
  normalizeEmbedDimension,
  resolveEmbedConfig,
  secondsToTimestamp,
  timestampToSeconds
} from "../../embedUtils";

import {
  CodeBlock,
  SectionLabel,
  OptionsCard,
  OptRow,
  NativeCheckbox,
  OptLabel,
  TimeInput,
  PresetRow,
  PresetLabel,
  PresetChipsGroup,
  Chip,
  DimRow,
  DimField,
  DimTag,
  DimInput,
  DimSep,
  DividerLine,
  CopyButton,
  CloseButton,
  HeaderRow,
  narrowScreen
} from "./EmbedDialog.styles";

const selectNodeText = node => {
  if (!node) {
    return;
  }

  const range = document.createRange();
  range.selectNodeContents(node);
  const sel = window.getSelection();
  sel.removeAllRanges();
  sel.addRange(range);
};

const filterTimeKeyDown = e => {
  if (e.ctrlKey || e.metaKey) {
    return;
  }

  const allowed = [
    "Backspace",
    "Delete",
    "Tab",
    "ArrowLeft",
    "ArrowRight",
    "Home",
    "End"
  ];

  if (allowed.includes(e.key)) {
    return;
  }

  if (e.key === ":") {
    const val = e.target.value;

    if ((val.match(/:/g) || []).length >= 2) {
      e.preventDefault();
    }

    return;
  }

  if (!/^\d$/.test(e.key)) {
    e.preventDefault();
  }
};

export const EmbedDialog = ({
  isOpen,
  onOpenChange,
  videoId,
  headline,
  embedConfig,
  initialStartSeconds = 0
}) => {
  const resolvedEmbedConfig = useMemo(
    () => resolveEmbedConfig(embedConfig),
    [embedConfig]
  );

  const [width, setWidth] = useState(resolvedEmbedConfig.defaultWidth);
  const [height, setHeight] = useState(resolvedEmbedConfig.defaultHeight);
  const [startAtEnabled, setStartAtEnabled] = useState(false);
  const [startTimestamp, setStartTimestamp] = useState("0:00");
  const [showCopied, setShowCopied] = useState(false);
  const codeBlockRef = useRef(null);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setWidth(resolvedEmbedConfig.defaultWidth);
    setHeight(resolvedEmbedConfig.defaultHeight);
    setStartAtEnabled(false);
    setStartTimestamp(secondsToTimestamp(initialStartSeconds));
    setShowCopied(false);
  }, [
    isOpen,
    initialStartSeconds,
    resolvedEmbedConfig.defaultHeight,
    resolvedEmbedConfig.defaultWidth
  ]);

  useEffect(() => {
    if (!showCopied) {
      return;
    }

    const timeoutId = setTimeout(() => setShowCopied(false), 2500);

    return () => clearTimeout(timeoutId);
  }, [showCopied]);

  const effectiveQueryParams = useMemo(() => {
    const secs = timestampToSeconds(startTimestamp);

    return mergeEmbedTimeQueryParam(
      resolvedEmbedConfig.queryParams,
      startAtEnabled,
      secs
    );
  }, [resolvedEmbedConfig.queryParams, startAtEnabled, startTimestamp]);

  const embedHtml = useMemo(() => {
    return getEmbedIframeHtml({
      videoId,
      width: normalizeEmbedDimension(width),
      height: normalizeEmbedDimension(height),
      domain: resolvedEmbedConfig.domain,
      queryParams: effectiveQueryParams,
      title: headline,
      getEmbedUrlOverride: resolvedEmbedConfig.getEmbedUrlOverride
    });
  }, [
    effectiveQueryParams,
    height,
    resolvedEmbedConfig.domain,
    resolvedEmbedConfig.getEmbedUrlOverride,
    videoId,
    headline,
    width
  ]);

  const activePresetIndex = useMemo(
    () =>
      EMBED_DIMENSION_PRESETS.findIndex(p =>
        matchesEmbedPreset(width, height, p)
      ),
    [height, width]
  );

  const handleCopy = useCallback(
    e => {
      e.stopPropagation();
      copyTextToClipboard(embedHtml, () => setShowCopied(true));
    },
    [embedHtml]
  );

  const stopPropagation = useCallback(e => {
    e.stopPropagation();
  }, []);

  const closeOverlay = useCallback(
    e => {
      stopPropagation(e);
      onOpenChange(false);
    },
    [stopPropagation, onOpenChange]
  );

  const handleSelectCode = useCallback(() => {
    selectNodeText(codeBlockRef.current);
  }, []);

  const handleStartChange = useCallback(e => {
    setStartAtEnabled(e.target.checked);
  }, []);

  const handleTimeChange = useCallback(e => {
    setStartTimestamp(e.target.value);
    setStartAtEnabled(true);
  }, []);

  const applyPreset = useCallback((w, h) => {
    setWidth(w);
    setHeight(h);
  }, []);

  const handleWidthBlur = useCallback(() => {
    setWidth(normalizeEmbedDimension(width));
  }, [width]);

  const handleHeightBlur = useCallback(() => {
    setHeight(normalizeEmbedDimension(height));
  }, [height]);

  return (
    <DialogRoot open={isOpen} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogOverlay onClick={closeOverlay} onPointerDown={closeOverlay} />
        <DialogContent
          onClick={stopPropagation}
          onPointerDown={stopPropagation}
          backgroundColor="$background"
          height="auto"
          css={{
            width: "min(640px, calc(100vw - 32px))",
            maxWidth: "min(640px, calc(100vw - 32px))",
            maxHeight: "calc(100vh - 48px)",
            overflowY: "auto",
            padding: "24px",
            boxShadow: "$500",
            borderRadius: "12px",
            color: "$onBackground",
            fontSize: "$087",
            lineHeight: "$meta",
            [narrowScreen]: {
              width: "calc(100vw - 16px)",
              maxWidth: "calc(100vw - 16px)",
              maxHeight: "calc(100vh - 16px)",
              padding: "16px",
              borderRadius: "10px"
            }
          }}
        >
          <DialogHeader
            css={{
              padding: 0,
              margin: 0,
              border: "none",
              background: "transparent"
            }}
          >
            <HeaderRow>
              <DialogTitle
                css={{
                  margin: 0,
                  fontSize: "$100",
                  fontWeight: "$bold",
                  lineHeight: "$125",
                  color: "$onBackground"
                }}
              >
                Embed Video
              </DialogTitle>
              <DialogClose asChild>
                <CloseButton
                  type="button"
                  aria-label="Close embed dialog"
                  onClick={stopPropagation}
                >
                  <Icon label="Close">
                    <Close />
                  </Icon>
                </CloseButton>
              </DialogClose>
            </HeaderRow>
          </DialogHeader>
          <DialogBody
            css={{
              padding: 0,
              margin: 0,
              background: "transparent",
              border: "none",
              maxHeight: "none",
              overflowY: "visible"
            }}
          >
            <CodeBlock
              ref={codeBlockRef}
              aria-label="Embed code"
              onClick={handleSelectCode}
            >
              {embedHtml}
            </CodeBlock>

            <SectionLabel>Embed Options</SectionLabel>

            <OptionsCard>
              <OptRow>
                <NativeCheckbox
                  id="embed-start-at"
                  type="checkbox"
                  checked={startAtEnabled}
                  onChange={handleStartChange}
                  onClick={stopPropagation}
                />
                <OptLabel htmlFor="embed-start-at">Start at</OptLabel>
                <TimeInput
                  id="embed-start-time"
                  type="text"
                  name="embed-dialog-start-time"
                  autoComplete="off"
                  aria-label="Start time"
                  spellCheck={false}
                  inactive={!startAtEnabled}
                  value={startTimestamp}
                  onChange={handleTimeChange}
                  onKeyDown={filterTimeKeyDown}
                  onClick={stopPropagation}
                />
              </OptRow>

              <PresetRow>
                <PresetLabel>Preset</PresetLabel>
                <PresetChipsGroup>
                  {EMBED_DIMENSION_PRESETS.map((p, i) => (
                    <Chip
                      key={p.id}
                      type="button"
                      active={activePresetIndex === i}
                      onClick={e => {
                        e.stopPropagation();
                        applyPreset(p.width, p.height);
                      }}
                    >
                      {p.label}
                    </Chip>
                  ))}
                </PresetChipsGroup>
              </PresetRow>

              <DimRow>
                <DimField>
                  <DimTag>W</DimTag>
                  <DimInput
                    id="embed-width"
                    type="text"
                    name="embed-dialog-width"
                    autoComplete="off"
                    spellCheck={false}
                    value={width}
                    onChange={e => setWidth(e.target.value)}
                    onBlur={handleWidthBlur}
                    onClick={stopPropagation}
                    aria-label="Width"
                  />
                </DimField>
                <DimSep aria-hidden>×</DimSep>
                <DimField>
                  <DimTag>H</DimTag>
                  <DimInput
                    id="embed-height"
                    type="text"
                    name="embed-dialog-height"
                    autoComplete="off"
                    spellCheck={false}
                    value={height}
                    onChange={e => setHeight(e.target.value)}
                    onBlur={handleHeightBlur}
                    onClick={stopPropagation}
                    aria-label="Height"
                  />
                </DimField>
              </DimRow>
            </OptionsCard>

            <DividerLine />
          </DialogBody>
          <DialogFooter
            css={{
              padding: 0,
              margin: 0,
              border: "none",
              background: "transparent"
            }}
          >
            <CopyButton
              type="button"
              copied={showCopied}
              onClick={handleCopy}
              aria-label={showCopied ? "Copied" : "Copy embed code"}
            >
              {showCopied ? (
                <>
                  <Icon label="Copied">
                    <Success />
                  </Icon>
                  Copied!
                </>
              ) : (
                <>
                  <Icon label="Copy">
                    <Copy />
                  </Icon>
                  Copy
                </>
              )}
            </CopyButton>
          </DialogFooter>
        </DialogContent>
      </DialogPortal>
    </DialogRoot>
  );
};

EmbedDialog.displayName = "EmbedDialog";

EmbedDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  videoId: PropTypes.string,
  headline: PropTypes.string,
  initialStartSeconds: PropTypes.number,
  embedConfig: PropTypes.shape({
    domain: PropTypes.string,
    defaultWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    defaultHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    queryParams: PropTypes.object,
    getEmbedUrlOverride: PropTypes.func
  })
};
